import * as React from 'react'
import { useMutationState } from '@tanstack/react-query'
import { Cookies, useCookies } from 'react-cookie'
import { CurrencyCode } from '@repo/graphql-types'
import { useUpdateUserCurrencyMutation } from 'codegen/generated/user-preferences'
import { CURRENCY_COOKIE_NAME } from 'constants/cookies'
import { getCookieDomain } from 'utils/env'
import { useUserQuery } from './auth'
import { isLoggedInQuerySelector } from '../selectors'

export function useSyncCurrencyCookie() {
  const {
    data: accountCurrency,
    isSuccess,
    isFetching,
  } = useUserQuery(undefined, {
    select: data =>
      !data.me?.errors?.length && isLoggedInQuerySelector(data)
        ? data.me?.user?.currency.code
        : undefined,
  })
  const [cookies, setCookie, removeCookie] = useCookies([CURRENCY_COOKIE_NAME])
  const currency = cookies[CURRENCY_COOKIE_NAME]
  const currencyUpdates = useMutationState({
    filters: { mutationKey: useUpdateUserCurrencyMutation.getKey() },
    select: mutation => mutation.state.status,
  })
  const isChangingCurrency = currencyUpdates.some(
    status => status === 'pending' || status === 'success',
  )
  const reloadRequested = React.useRef(false)

  React.useEffect(() => {
    // Currency selectors reload after saving; their cached user response may still have the old currency.
    if (
      !isSuccess ||
      isFetching ||
      isChangingCurrency ||
      reloadRequested.current ||
      !accountCurrency
    ) {
      return
    }

    if (!Object.values(CurrencyCode).includes(accountCurrency)) {
      return
    }

    if (typeof currency === 'string' && currency.toLowerCase() === accountCurrency.toLowerCase()) {
      return
    }

    const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN || process.env.VITE_COOKIE_DOMAIN
    if (!cookieDomain) {
      return
    }

    // WordPress can leave narrower cookies that shadow the shared-domain value.
    removeCookie(CURRENCY_COOKIE_NAME, { path: '/' })
    removeCookie(CURRENCY_COOKIE_NAME, { path: '/', domain: 'staging.creativefabrica.com' })
    const value = accountCurrency.toUpperCase()
    setCookie(CURRENCY_COOKIE_NAME, value, {
      domain: getCookieDomain(cookieDomain),
      path: '/',
      maxAge: 60 * 60 * 24 * 365,
    })

    if (new Cookies().get(CURRENCY_COOKIE_NAME) === value) {
      reloadRequested.current = true
      window.location.reload()
    }
  }, [
    accountCurrency,
    currency,
    isSuccess,
    isFetching,
    isChangingCurrency,
    setCookie,
    removeCookie,
  ])
}
